import React, { useContext } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
	MessageCircle,
	Contact,
	KanbanSquare,
	LayoutDashboard,
	Menu as MenuIcon,
} from "lucide-react";

import { cn } from "../lib/utils";
import useTickets from "../hooks/useTickets";
import { AuthContext } from "../context/Auth/AuthContext";

// Barra de navegação de baixo, como em aplicativo de celular.
//
// No celular o menu inteiro ficava atrás de um botão de sanduíche: trocar de
// tela custava dois toques e uma gaveta cobrindo a tela. Aqui os quatro
// destinos mais usados ficam a um toque, na parte de baixo — onde o polegar
// alcança sem trocar a mão de posição.
//
// Quatro atalhos e o "Mais", que abre o menu completo: acima de cinco, os
// alvos ficam estreitos demais para o dedo e um item é tocado no lugar do
// vizinho.
const ITENS = [
	{ to: "/tickets", rotulo: "Conversas", Icone: MessageCircle, contador: true },
	{ to: "/contacts", rotulo: "Contatos", Icone: Contact },
	{ to: "/kanban", rotulo: "Kanban", Icone: KanbanSquare },
	{ to: "/", rotulo: "Painel", Icone: LayoutDashboard, exato: true },
];

const BottomNav = ({ onAbrirMenu }) => {
	const location = useLocation();
	const { user } = useContext(AuthContext);

	// Mesma fonte que o sino do topo usa, então os dois números concordam.
	const { tickets } = useTickets({ withUnreadMessages: "true" });
	const naoLidas = user?.id ? tickets?.length || 0 : 0;

	// Dentro de uma conversa a barra sai da frente: ali a tela inteira é a
	// conversa, e a caixa de digitar não pode dividir o rodapé com outra coisa
	// — é assim que todo aplicativo de mensagem se comporta.
	const dentroDaConversa = /^\/tickets\/\d+/.test(location.pathname);

	if (dentroDaConversa) return null;

	return (
		<nav
			aria-label="Navegação principal"
			// pb com a safe area: no iPhone sem botão, o indicador de início
			// come a faixa de baixo e engoliria metade dos rótulos.
			className="flex shrink-0 items-stretch border-t bg-card pb-[env(safe-area-inset-bottom)] md:hidden"
		>
			{ITENS.map(({ to, rotulo, Icone, contador, exato }) => (
				<NavLink
					key={to}
					to={to}
					end={exato}
					className={({ isActive }) =>
						cn(
							// min-h-14: alvo de toque confortável. Abaixo de ~44px o
							// dedo acerta o vizinho.
							"relative flex min-h-14 flex-1 flex-col items-center justify-center gap-0.5 px-1 py-1.5 text-[11px] font-medium transition-colors",
							isActive
								? "text-primary"
								: "text-muted-foreground hover:text-foreground"
						)
					}
				>
					{({ isActive }) => (
						<>
							{/* Traço no topo do item ativo: em tela pequena, cor
							    sozinha é pouco para dizer onde se está. */}
							<span
								className={cn(
									"absolute inset-x-3 top-0 h-0.5 rounded-full",
									isActive ? "bg-primary" : "bg-transparent"
								)}
							/>
							<span className="relative">
								<Icone className="h-[22px] w-[22px]" />
								{contador && naoLidas > 0 && (
									<span className="absolute -right-2 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-bold leading-none text-destructive-foreground">
										{naoLidas > 99 ? "99+" : naoLidas}
									</span>
								)}
							</span>
							<span className="truncate">{rotulo}</span>
						</>
					)}
				</NavLink>
			))}

			<button
				type="button"
				onClick={onAbrirMenu}
				className="flex min-h-14 flex-1 flex-col items-center justify-center gap-0.5 px-1 py-1.5 text-[11px] font-medium text-muted-foreground transition-colors hover:text-foreground"
			>
				<MenuIcon className="h-[22px] w-[22px]" />
				<span>Mais</span>
			</button>
		</nav>
	);
};

export default BottomNav;
